import type { LabelValuePair } from '../../foundation/utils/types';
import type { UserProfile } from '../authentication/redux/types';
import type {
  ExternalDatabase,
  ExternalDatabaseClassification,
  MedicalInvestigation,
  MolecularImpactHeaderDescriptionType,
  PredictionToolType,
} from '../variant_workflow/redux';
import { apiSlice } from './apiSlice';

/**
 * RTK Query endpoints for global / reference data.
 *
 * These are static lookup tables fetched once after login and kept for the
 * entire session (`keepUnusedDataFor: Infinity`).
 *
 * The data is now loaded by the root route `loader` in
 * `features/startup/Startup.tsx`. Components read the cached values through
 * the auto-generated query hooks below.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type ZygosityOption = LabelValuePair;
export type SegregationOption = LabelValuePair;
export type ReportVariantCategoryOption = LabelValuePair;
export type PublicationType = LabelValuePair;

// ─── Injected Endpoints ──────────────────────────────────────────────────────

export const globalDataApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getZygosityOptions: builder.query<ZygosityOption[], void>({
      query: () => ({ url: '/variantdata/zygosity-options' }),
      providesTags: [{ type: 'GlobalData', id: 'ZygosityOptions' }],
      keepUnusedDataFor: Infinity,
    }),

    getSegregationOptions: builder.query<SegregationOption[], void>({
      query: () => ({ url: '/resultdata/segregation-options' }),
      providesTags: [{ type: 'GlobalData', id: 'SegregationOptions' }],
      keepUnusedDataFor: Infinity,
    }),

    getMedicalInvestigations: builder.query<MedicalInvestigation[], void>({
      query: () => ({ url: '/resultdata/medicalinvestigations' }),
      providesTags: [{ type: 'GlobalData', id: 'MedicalInvestigations' }],
      keepUnusedDataFor: Infinity,
    }),

    getReportVariantCategoryOptions: builder.query<
      ReportVariantCategoryOption[],
      void
    >({
      query: () => ({ url: '/resultdata/reportvariantcategory-options' }),
      providesTags: [
        { type: 'GlobalData', id: 'ReportVariantCategoryOptions' },
      ],
      keepUnusedDataFor: Infinity,
    }),

    getExternalDatabases: builder.query<ExternalDatabase[], void>({
      query: () => ({ url: '/variantdata/externaldatabase' }),
      providesTags: [{ type: 'GlobalData', id: 'ExternalDatabases' }],
      keepUnusedDataFor: Infinity,
    }),

    getExternalDatabaseClassifications: builder.query<
      ExternalDatabaseClassification[],
      void
    >({
      query: () => ({
        url: '/variantdata/variant/externaldatabase_classifications',
      }),
      providesTags: [
        { type: 'GlobalData', id: 'ExternalDatabaseClassifications' },
      ],
      keepUnusedDataFor: Infinity,
    }),

    getPredictionTools: builder.query<PredictionToolType[], void>({
      query: () => ({ url: '/variantdata/predictiontool' }),
      providesTags: [{ type: 'GlobalData', id: 'PredictionTools' }],
      keepUnusedDataFor: Infinity,
    }),

    getPublicationTypes: builder.query<PublicationType[], void>({
      query: () => ({ url: '/variantdata/publication-types' }),
      providesTags: [{ type: 'GlobalData', id: 'PublicationTypes' }],
      keepUnusedDataFor: Infinity,
    }),

    getMolecularImpactHeaderDescriptions: builder.query<
      MolecularImpactHeaderDescriptionType[],
      void
    >({
      query: () => ({
        url: '/variantdata/variant/molecular_impact_header_descriptions',
      }),
      providesTags: [
        { type: 'GlobalData', id: 'MolecularImpactHeaderDescriptions' },
      ],
      keepUnusedDataFor: Infinity,
    }),

    // ── APP VERSION ───────────────────────────────────────────────────────────
    // Fetched from the backend's settings.APPVERSION
    // settings.APPVERSION is replaced during deployment
    getAppVersion: builder.query<{ version: string }, void>({
      query: () => ({ url: '/appversion' }),
      providesTags: [{ type: 'GlobalData', id: 'AppVersion' }],
      keepUnusedDataFor: Infinity,
    }),

    // ── User profile ──────────────────────────────────────────────────────────
    // Equivalent to the tutorial's getUsers endpoint.
    // Fetched once after login by dispatching
    // globalDataApi.endpoints.getUserDetails.initiate({ username }) from Startup.tsx
    // The useGetUserDetailsQuery hook is exported for convenience but the
    // primary consumer reads via globalDataApi.endpoints.getUserDetails.select()
    getUserDetails: builder.query<UserProfile, { username: string }>({
      query: ({ username }) => ({ url: `/users/username/${username}` }),
      providesTags: [{ type: 'UserProfile' }],
      keepUnusedDataFor: Infinity,
    }),
  }),
});

// ─── Auto-generated Hooks ────────────────────────────────────────────────────

export const {
  useGetZygosityOptionsQuery,
  useGetSegregationOptionsQuery,
  useGetMedicalInvestigationsQuery,
  useGetReportVariantCategoryOptionsQuery,
  useGetExternalDatabasesQuery,
  useGetExternalDatabaseClassificationsQuery,
  useGetPredictionToolsQuery,
  useGetPublicationTypesQuery,
  useGetMolecularImpactHeaderDescriptionsQuery,
  useGetAppVersionQuery,
  useGetUserDetailsQuery,
} = globalDataApi;
